/**
 * Briefing — TanStack Query hooks for the "Brief & Deep" pattern.
 *
 * Wires the typed endpoint helpers in ``@ando/core/api/endpoints/briefing``
 * to TanStack Query. Cache semantics:
 *
 *   - ``staleTime``: 5 minutes — briefings are AI-generated summaries that
 *     don't need second-by-second freshness. The user can force-refresh via
 *     the explicit refresh button on the aggregate card.
 *   - ``refetchOnWindowFocus: true`` — when the user comes back to the tab
 *     after a coffee break we want a fresh briefing without manual reload.
 *   - ``retry: 1`` — single retry; BE may not be deployed yet (Track A in
 *     parallel) and we don't want to thrash the network with 404 loops.
 *
 * Defensive contract — when the BE returns 404/5xx the hooks expose
 * ``isError`` and the components fall back to a silent empty state; the
 * surface chrome never breaks. See ``BriefCard`` / ``BriefAggregate``.
 */

import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  type AggregateBriefResponse,
  type BriefDomain,
  type BriefWindow,
  type DomainBrief,
  getAggregateBrief,
  getDomainBrief,
} from "@ando/core";
import { useCallback } from "react";

const BRIEF_KEY = "brief" as const;

const STALE_MS = 5 * 60_000;

export function inboxBriefQueryKey(window: BriefWindow): readonly unknown[] {
  return domainBriefQueryKey("inbox", window);
}

export function domainBriefQueryKey(
  domain: BriefDomain,
  window: BriefWindow,
): readonly unknown[] {
  return [BRIEF_KEY, domain, window] as const;
}

export function aggregateBriefQueryKey(window: BriefWindow): readonly unknown[] {
  return [BRIEF_KEY, "aggregate", window] as const;
}

export interface UseBriefResult<T> {
  readonly data: T | undefined;
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly error: unknown;
  readonly refetch: () => Promise<unknown>;
}

/** Inbox-domain brief — drives the hero card on ``/workspace/conversations``. */
export function useInboxBrief(
  window: BriefWindow = "morning",
): UseBriefResult<DomainBrief> {
  return useDomainBrief("inbox", window);
}

/**
 * Generic per-domain brief — drives every ``BriefingSlot`` instance across
 * the workspace. ``inbox`` is the only domain whose endpoint is live today;
 * the other domains 404 silently and the slot renders ``null`` (see the
 * defensive contract comment at the top of this file).
 */
export function useDomainBrief(
  domain: BriefDomain,
  window: BriefWindow = "morning",
): UseBriefResult<DomainBrief> {
  const query = useQuery({
    queryKey: domainBriefQueryKey(domain, window),
    queryFn: () => getDomainBrief(domain, window),
    staleTime: STALE_MS,
    refetchOnWindowFocus: true,
    // Domain endpoints other than `inbox` are not yet BE-shipped (May
    // 2026 status) → they 404. The slot renders `null` for missing
    // data; suppress retries + don't log noisy errors. When a domain
    // ships we just enable retries for that domain on the call-site.
    retry: (failureCount, err) => {
      const status = (err as { status?: number } | null)?.status;
      if (status === 404) return false;
      return failureCount < 1;
    },
    throwOnError: false,
  });
  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: () => query.refetch(),
  };
}

/** Master aggregate — drives the top-of-dashboard summary card. */
export function useAggregateBrief(
  window: BriefWindow = "morning",
): UseBriefResult<AggregateBriefResponse> {
  const query = useQuery({
    queryKey: aggregateBriefQueryKey(window),
    queryFn: () => getAggregateBrief(window),
    staleTime: STALE_MS,
    refetchOnWindowFocus: true,
    retry: 1,
  });
  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: () => query.refetch(),
  };
}

/** Imperative invalidation — call after an action card execution that
 * mutates state behind the brief (archive, snooze, reply). */
export function useInvalidateBrief(): () => void {
  const qc = useQueryClient();
  return useCallback(() => {
    void qc.invalidateQueries({ queryKey: [BRIEF_KEY] });
  }, [qc]);
}
